import * as d3 from "d3";
import DiagramClassElement from "./DiagramClassElement.tsx";
import {DiagramRelationshipElement} from "./DiagramRelationshipElement.tsx";
import { getTheme } from "@semantical/theme.ts";
import {
    SchemaClass,
    ObjectProperty,
    OwlClassExpression,
    classExpressionSatisfied,
    isNamedClass,
    isUnionOf,
    isIntersectionOf,
    getNamedClassUris,
    iriToString,
    ClassTaxonomyNode, loadClassTaxonomy, loadClassHierarchy, ClassHierarchyMap, isSubclassOf,
    loadRelationshipTaxonomy,
    loadDataProperties, ClassDataPropertiesMap,
    loadObjectPropertyRestrictions, ClassObjectPropertyRestrictionsMap
} from "@semantical/modules/ontology-model";
import { Individual, Relationship, RELATIONSHIP_TYPE } from "@semantical/modules/knowledge-graph";
import type { Node, Element } from "@semantical/modules/semantical-core";
import { isNode, isEdge } from "@semantical/modules/semantical-core";
import {Concept} from "@semantical/modules/vocabulary";
import React from "react";
// import RelationshipCommandPalette from "@semantical/components/diagram-editor/RelationshipCommandPalette.tsx";
import RelationshipCommandPaletteHierarchical, {RelationshipTaxonomyNode} from "@semantical/components/diagram-editor/RelationshipCommandPaletteHierarchical.tsx";
import ClassPicker from "@semantical/components/diagram-editor/ClassPicker.tsx";
import NameEditor from "@semantical/components/diagram-editor/NameEditor.tsx";
import ConfirmDialog from "@semantical/components/diagram-editor/ConfirmDialog.tsx";
import Diagram, { RelationshipStyle, AnchorMode } from "@semantical/modules/diagram/Diagram.ts";
import DiagramClass from "@semantical/modules/diagram/DiagramClass.ts";
import DiagramRelationship from "@semantical/modules/diagram/DiagramRelationship.ts";
import {ForceLayoutEngine} from "@semantical/components/diagram-editor/ForceLayoutEngine.ts";
import LiquidBlobLayer, { BlobElement } from "@semantical/components/diagram-editor/LiquidBlobLayer.tsx";

function snapToGrid(value: number, GRID_SIZE: number) {
    return Math.round(value / GRID_SIZE) * GRID_SIZE;
}

/**
 * Get the SchemaClass from a Node (works for both SchemaClass and Individual)
 */
function getSchemaClassFromElement(element: Node): SchemaClass | undefined {
    if (element instanceof Individual) {
        return element.modelClass;
    } else if (element instanceof SchemaClass) {
        return element;
    }
    return undefined;
}

/**
 * Get a human-readable label from a class expression
 */
function getClassExpressionLabel(expr: OwlClassExpression | undefined): string {
    if (!expr) return 'unknown';

    const extractLocalName = (uri: string) =>
        uri.split('#').pop() || uri.split('/').pop() || uri;

    if (isNamedClass(expr)) {
        const uri = iriToString(expr.iri);
        return extractLocalName(uri);
    }
    if (isUnionOf(expr)) {
        const names = getNamedClassUris(expr).map(extractLocalName);
        return names.join(' or ');
    }
    if (isIntersectionOf(expr)) {
        const names = getNamedClassUris(expr).map(extractLocalName);
        return names.join(' and ');
    }
    return 'unknown';
}

function getTranslateValues(transformString: string) {
    // Extracts the translate(x,y) values from the transform string
    const match = /translate\(([^,]+),([^,]+)\)/.exec(transformString);
    return match ? {x: parseFloat(match[1]), y: parseFloat(match[2])} : {x: 0, y: 0};
}

let dx0 = 0, dy0 = 0;

type Props = {
    /** Currently selected element (from App state) */
    selectedElement?: Element | null
    onClassSelected: { (diagramClass: DiagramClass | null): void }
    onRelationshipSelected?: { (diagramRelationship: DiagramRelationship | null): void }
    gridSize: number
    diagram: Diagram
    onClassAdded: { (diagramClass: DiagramClass): void }
    onClassMoved: { (diagramClass: DiagramClass, x:number, y:number): void }
    onRelationshipAdded: { (diagramRelationship: DiagramRelationship): void }
    onRelationshipReconnected?: { (diagramRelationship: DiagramRelationship, isSource: boolean, newClass: DiagramClass): void }
    onClassDeleted?: { (diagramClass: DiagramClass): void }
    onRelationshipDeleted?: { (diagramRelationship: DiagramRelationship): void }
    onClassHidden?: { (diagramClass: DiagramClass): void }
    onUnhideAll?: { (): void }
    onPlacementCancelled?: { (): void }
    onDiagramChanged?: { (): void }
    onElementRenamed?: { (element: Node, newName: string): void }
    editingMode?: 'TBox' | 'ABox'
    generateIndividualUri?: { (name: string): string }
    domainTtl: string
    domainNs: string
    allowedNamespaces?: string[]
    showOptionalProperties?: boolean
    showEssentialProperties?: boolean
    showStereotype?: boolean
    darkMode?: boolean
    // Pre-loaded taxonomy data (avoids re-parsing TTL)
    relationshipTaxonomy?: RelationshipTaxonomyNode[]
    classTaxonomy?: ClassTaxonomyNode[]
    dataPropertiesMap?: ClassDataPropertiesMap
    objectPropertyRestrictionsMap?: ClassObjectPropertyRestrictionsMap
    classHierarchy?: ClassHierarchyMap
    // Map of predicate URIs to highlight colors for WordCloud feature
    highlightedPredicates?: Map<string, string>
    // Resolver for getting class category colors by URI
    getClassColor?: (classUri: string) => string | undefined
    // Resolver for getting class labels by URI (for SchemaIndividual stereotypes)
    getClassLabel?: (classUri: string) => string | undefined
    // Resolver for getting namespace tag info (prefix and color) by source graph IRI
    getNamespaceTag?: (sourceGraphIRI: string) => { prefix?: string; color?: string } | undefined
}

type PickerState = {
    visible: boolean;
    source: DiagramClass | null;
    target: DiagramClass | null;
    position: { x: number; y: number };
};

type ClassPickerState = {
    visible: boolean;
    modelClass: Node | null;
    position: { x: number; y: number };
};

type NameEditorState = {
    visible: boolean;
    modelClass: Node | null;
    position: { x: number; y: number };
    defaultName: string;
};

type ReconnectConfirmState = {
    visible: boolean;
    relationship: DiagramRelationship | null;
    isSource: boolean;
    newClass: DiagramClass | null;
    message: string;
};

type State = {
    "selectedClass": DiagramClassElement | null
    "selectedRelationship": DiagramRelationship | null
    zoomK: number
    showHelp: boolean
    picker: PickerState
    classPicker: ClassPickerState
    nameEditor: NameEditorState
    relationshipTaxonomy: RelationshipTaxonomyNode[] | null
    classTaxonomy: ClassTaxonomyNode[] | null
    dataPropertiesMap: ClassDataPropertiesMap | null
    objectPropertyRestrictionsMap: ClassObjectPropertyRestrictionsMap | null
    classHierarchy: ClassHierarchyMap | null
    // Class being hovered during relationship reconnection
    reconnectHoverClass: DiagramClass | null
    // Confirmation dialog for reconnection when constraints mismatch
    reconnectConfirm: ReconnectConfirmState
    // Whether name editor is in rename mode (vs create mode)
    isRenaming: boolean
}

export default class DiagramEditor extends React.Component<Props, State> {
    "svg": React.RefObject<SVGSVGElement | null>;
    "svgViewport": React.RefObject<SVGGElement | null>
    "svgClasses": React.RefObject<SVGGElement | null>
    private zoomBehavior!: d3.ZoomBehavior<SVGSVGElement, any>;
    "dragActive" = false
    "dragActiveElement": d3.Selection<SVGGElement, any, any, any> | null = null
    "dragActiveModel": Node | null = null
    "relationships": DiagramRelationshipElement[] = []
    "selectedRelationships": DiagramRelationship[] = []
    "onUnselectAllClasses" = null
    private zoomStartPosition: {x: number, y: number} | null = null
    private zoomMoveDistance = 0
    private classDragStartPosition: {x: number, y: number} | null = null
    private classDragDistance = 0
    private isDraggingClass = false
    private readonly CLICK_MOVE_THRESHOLD = 5 // pixels
    private pendingPlacement: { x: number, y: number, modelClass: Node } | null = null
    private forceLayoutEngine: ForceLayoutEngine | null = null

    state: State = {
        "selectedClass": null,
        "selectedRelationship": null,
        zoomK: 1,
        showHelp: false,
        picker: {
            visible: false,
            source: null,
            target: null,
            position: { x: 0, y: 0 }
        },
        classPicker: {
            visible: false,
            modelClass: null,
            position: { x: 0, y: 0 }
        },
        nameEditor: {
            visible: false,
            modelClass: null,
            position: { x: 0, y: 0 },
            defaultName: ""
        },
        relationshipTaxonomy: null,
        classTaxonomy: null,
        dataPropertiesMap: null,
        objectPropertyRestrictionsMap: null,
        classHierarchy: null,
        reconnectHoverClass: null,
        reconnectConfirm: {
            visible: false,
            relationship: null,
            isSource: false,
            newClass: null,
            message: ""
        },
        isRenaming: false
    }

    getDiagram() {
        return this.props.diagram;
    }


    constructor(props: Props) {
        super(props);

        this.svg = React.createRef<SVGSVGElement>();
        this.svgClasses = React.createRef<SVGGElement>();
        this.svgViewport = React.createRef<SVGGElement>();
    }

    componentDidMount() {
        console.debug("DiagramEditor mounted");

        this.loadDomainData();

        if (this.svg.current == null) return; // svg ref is not set

        const diagramEditor = this;
        const svg = d3.select(this.svg.current);

        const zoom: d3.ZoomBehavior<SVGSVGElement, any> = d3.zoom<SVGSVGElement, any>()
            .filter(() => !diagramEditor.dragActive)
            .clickDistance(this.CLICK_MOVE_THRESHOLD) // Allow small movements to still trigger clicks
            .on("start", (event) => {
                // Track starting position to calculate movement distance
                this.zoomStartPosition = {x: event.sourceEvent?.clientX || 0, y: event.sourceEvent?.clientY || 0};
                this.zoomMoveDistance = 0;
            })
            .on("zoom", (event) => {
                // Calculate how far we've moved during the zoom/pan
                if (this.zoomStartPosition && event.sourceEvent) {
                    const dx = event.sourceEvent.clientX - this.zoomStartPosition.x;
                    const dy = event.sourceEvent.clientY - this.zoomStartPosition.y;
                    this.zoomMoveDistance = Math.sqrt(dx * dx + dy * dy);
                }
                this.handleViewportZoom(event);
            })
            .on("end", () => {
                // Persist viewport transform when pan/zoom interaction ends
                this.props.onDiagramChanged?.();
            });

        // Save reference for programmatic transforms
        this.zoomBehavior = zoom;

        svg
            .call(zoom)
            .on("click", (event) => this.handleDiagramClick(event))
            .on("dblclick.zoom", null) // disable double click zoom
            .on("dblclick", () => svg.call(zoom.transform as any, d3.zoomIdentity))
            .on("mousemove", (event) => this.handleDiagramMouseMove(event))
            .on("contextmenu", (event) => this.handleContextMenu(event));

        // Restore viewport transform from diagram if available
        const vt = this.props.diagram.viewportTransform;
        if (vt && (vt.x !== 0 || vt.y !== 0 || vt.k !== 1)) {
            svg.call(zoom.transform as any, d3.zoomIdentity.translate(vt.x, vt.y).scale(vt.k));
        }

        // Attach resize event listener
        window.addEventListener("resize", () => this.onResize());

        // Attach keyboard event listener for hotkeys
        document.addEventListener("keydown", this.handleKeyDown);

        // Restore force layout state from diagram model
        if (this.props.diagram.forceLayoutEnabled) {
            // Use setTimeout to ensure the component is fully mounted
            setTimeout(() => {
                // Use setForceLayout with persist=false and low alpha to preserve existing positions
                this.setForceLayout(true, false, 0.01);
            }, 100);
        }
    }

    render() {
        const theme = getTheme();
        console.debug("DiagramEditor render");

        const isDark = this.props.darkMode !== false;

        // Get visible classes (filter out hidden ones)
        const visibleClasses = this.props.diagram.getVisibleClasses();

        // Get visible relationships (both source and target must be visible)
        const allRelationships = this.props.diagram.getAllVisibleRelationships();

        // Check if there are hidden classes (for showing Unhide All button)
        const hasHiddenClasses = this.props.diagram.hasHiddenClasses();

        // Group edges by source-target pair to detect multiple edges between same nodes
        const edgeIndexMap = new Map<DiagramRelationship, { index: number; total: number }>();
        const edgeGroups = new Map<string, DiagramRelationship[]>();
        for (const rel of allRelationships) {
            // Use sorted URIs to treat A→B and B→A as same pair
            const key = [rel.source.modelElement.uri, rel.target.modelElement.uri].sort().join('|');
            if (!edgeGroups.has(key)) edgeGroups.set(key, []);
            edgeGroups.get(key)!.push(rel);
        }
        for (const group of edgeGroups.values()) {
            // Sort each group: asserted relationships first (stable order), then inferred
            // This ensures asserted relationships keep their arc direction when inference adds symmetric edges
            group.sort((a, b) => {
                const aInferred = a.modelRelationship.isInferred ? 1 : 0;
                const bInferred = b.modelRelationship.isInferred ? 1 : 0;
                return aInferred - bInferred;
            });
            group.forEach((rel, i) => edgeIndexMap.set(rel, { index: i, total: group.length }));
        }

        const toolbarStyle: React.CSSProperties = {
            position: 'absolute', top: 10, left: 10, display: 'flex', gap: 6,
            background: isDark ? 'rgba(24, 26, 32, 0.95)' : 'rgba(255, 255, 255, 0.98)',
            padding: '6px 8px', borderRadius: 8,
            color: isDark ? '#fff' : '#334155',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: 11,
            border: isDark ? '1px solid rgba(100, 255, 180, 0.15)' : '1px solid #E2E8F0',
            boxShadow: isDark ? '0 2px 8px rgba(0,0,0,0.3)' : '0 2px 8px rgba(100, 120, 140, 0.15)'
        };
        const buttonBase: React.CSSProperties = {
            padding: '5px 10px', borderRadius: 5, cursor: 'pointer',
            border: isDark ? 'none' : '1px solid #E2E8F0',
            fontSize: 11, fontWeight: 500, transition: 'all 0.15s ease',
            display: 'flex', alignItems: 'center', gap: 4
        };
        const buttonOff: React.CSSProperties = {
            ...buttonBase,
            background: isDark ? 'rgba(255,255,255,0.06)' : '#F1F5F9',
            color: isDark ? 'rgba(255,255,255,0.5)' : '#94A3B8'
        };
        const buttonOn: React.CSSProperties = {
            ...buttonBase,
            background: isDark ? 'rgba(100, 255, 180, 0.15)' : '#E0F2F1',
            color: isDark ? 'rgba(100, 255, 180, 0.9)' : '#0F766E',
            borderColor: isDark ? 'transparent' : '#3B8298'
        };
        const buttonAction: React.CSSProperties = {
            ...buttonBase,
            background: isDark ? 'rgba(255,255,255,0.08)' : '#F1F5F9',
            color: isDark ? 'rgba(255,255,255,0.75)' : '#475569'
        };
        const buttonInfo: React.CSSProperties = {
            ...buttonBase,
            background: 'transparent',
            color: isDark ? 'rgba(255,255,255,0.4)' : '#94A3B8',
            cursor: 'default', padding: '5px 8px', border: 'none'
        };
        const selectStyle: React.CSSProperties = {
            background: isDark ? 'rgba(255,255,255,0.08)' : '#F1F5F9',
            color: isDark ? 'rgba(255,255,255,0.75)' : '#475569',
            padding: '5px 8px', borderRadius: 5, cursor: 'pointer',
            border: isDark ? 'none' : '1px solid #E2E8F0',
            fontSize: 11, fontWeight: 500,
            outline: 'none'
        };
        const helpStyle: React.CSSProperties = {
            position: 'absolute', top: 52, left: 10,
            background: isDark ? 'rgba(24, 26, 32, 0.95)' : 'rgba(255, 255, 255, 0.98)',
            color: isDark ? 'rgba(255,255,255,0.75)' : '#475569',
            padding: '12px 14px', borderRadius: 8, maxWidth: 300, lineHeight: 1.5,
            border: isDark ? '1px solid rgba(100, 255, 180, 0.15)' : '1px solid #E2E8F0',
            boxShadow: isDark ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(100, 120, 140, 0.15)',
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", fontSize: 11
        };

        return (
            <div id="diagram" className={`relative w-full h-full z-0 select-none [&_*]:select-none ${this.dragActive ? 'cursor-crosshair [&_*]:!cursor-crosshair' : ''}`}>
                <svg ref={this.svg} width="100%" height="100%">
                    <g ref={this.svgViewport} className={"viewport"}>
                        <rect id="grid" width="100000" height="100000" x="-50000" y="-50000"
                              style={{display: this.props.diagram.showGrid ? 'block' : 'none'}}
                              fill="url(#gridPattern)"></rect>
                        {this.props.diagram.showDomainVisualization && (
                            <LiquidBlobLayer
                                elements={this.prepareBlobElements(visibleClasses)}
                                groups={[
                                    {
                                        id: 'kg',
                                        color: isDark
                                            ? 'rgba(0, 200, 180, 0.15)'
                                            : 'rgba(59, 130, 152, 0.12)'
                                    },
                                    {
                                        id: 'tbox',
                                        color: isDark
                                            ? 'rgba(200, 100, 255, 0.15)'
                                            : 'rgba(150, 80, 200, 0.12)'
                                    }
                                ]}
                                enabled={this.props.diagram.showDomainVisualization}
                            />
                        )}
                        <g ref={this.svgClasses} className={"classes"}>
                            {/* Non-selected, non-highlighted relationships (bottom layer) */}
                            {allRelationships.map((r, index) => {
                                const edgeInfo = edgeIndexMap.get(r) || { index: 0, total: 1 };
                                const isInferred = r.isInferred;
                                const isRelSelected = !isInferred && (this.state.selectedRelationship === r || (isEdge(this.props.selectedElement) && this.props.selectedElement.id === r.modelRelationship.id));
                                const selectedNodeUri = isNode(this.props.selectedElement) ? this.props.selectedElement.uri : null;
                                const isOutgoingFromSelected = selectedNodeUri && r.source.modelElement.uri === selectedNodeUri;
                                // Skip if selected or outgoing from selected node (rendered in top layer)
                                if (isRelSelected || isOutgoingFromSelected) return null;
                                return (
                                    <DiagramRelationshipElement
                                        key={isInferred ? `inferred-${index}` : index}
                                        relationship={r}
                                        edgeIndex={edgeInfo.index}
                                        edgeTotal={edgeInfo.total}
                                        selected={false}
                                        onRelationshipSelected={isInferred ? undefined : () => this.selectRelationship(r)}
                                        onAnchorMoved={isInferred ? undefined : () => this.props.onDiagramChanged?.()}
                                        onAnchorReconnected={isInferred ? undefined : (rel, isSource, newClass) => this.handleRelationshipReconnect(rel, isSource, newClass)}
                                        allClasses={this.props.diagram.classes}
                                        onClassHover={isInferred ? undefined : (c) => this.handleReconnectClassHover(c)}
                                        highlightedClass={this.state.reconnectHoverClass}
                                        checkReconnectCompatibility={isInferred ? undefined : (newClass, isSource) => this.checkReconnectCompatibility(r, newClass, isSource)}
                                        isInferred={isInferred}
                                        relationshipStyle={this.props.diagram.relationshipStyle}
                                        anchorMode={this.props.diagram.anchorMode}
                                        selectedNodeUri={selectedNodeUri}
                                        highlightedPredicates={this.props.highlightedPredicates}
                                    />
                                );
                            })}
                            {/* Classes (middle layer) */}
                            {visibleClasses.map((c) => (
                                <DiagramClassElement
                                    key={c.modelElement.uri}
                                    diagramEditor={this}
                                    diagramClass={c}
                                    isSelected={isNode(this.props.selectedElement) && this.props.selectedElement.uri === c.modelElement.uri}
                                    reconnectHighlight={this.state.reconnectHoverClass === c}
                                    showOptionalProperties={this.props.showOptionalProperties ?? true}
                                    showEssentialProperties={this.props.showEssentialProperties ?? true}
                                    showStereotype={this.props.showStereotype ?? true}
                                    onAddRelationship={(predicate, source, target) => this.addRelationship(predicate, source, target)}
                                    onShowRelationshipPicker={(source, target, position) => this.showRelationshipPicker(source, target, position)}
                                    getClassColor={this.props.getClassColor}
                                    getClassLabel={this.props.getClassLabel}
                                    getNamespaceTag={this.props.getNamespaceTag}
                                    getDataPropertiesForClass={(classUri) => this.state.dataPropertiesMap?.get(classUri)}
                                    getObjectPropertyRestrictionsForClass={(classUri) => this.state.objectPropertyRestrictionsMap?.get(classUri)}
                                />
                            ))}
                            {/* Selected relationship and outgoing relationships from selected node (top layer) */}
                            {allRelationships.map((r, index) => {
                                const edgeInfo = edgeIndexMap.get(r) || { index: 0, total: 1 };
                                const isInferred = r.isInferred;
                                const isRelSelected = !isInferred && (this.state.selectedRelationship === r || (isEdge(this.props.selectedElement) && this.props.selectedElement.id === r.modelRelationship.id));
                                const selectedNodeUri = isNode(this.props.selectedElement) ? this.props.selectedElement.uri : null;
                                const isOutgoingFromSelected = selectedNodeUri && r.source.modelElement.uri === selectedNodeUri;
                                // Only render if selected or outgoing from selected node
                                if (!isRelSelected && !isOutgoingFromSelected) return null;
                                return (
                                    <DiagramRelationshipElement
                                        key={`top-${isInferred ? `inferred-${index}` : index}`}
                                        relationship={r}
                                        edgeIndex={edgeInfo.index}
                                        edgeTotal={edgeInfo.total}
                                        selected={isRelSelected}
                                        onRelationshipSelected={isInferred ? undefined : () => this.selectRelationship(r)}
                                        onAnchorMoved={isInferred ? undefined : () => this.props.onDiagramChanged?.()}
                                        onAnchorReconnected={isInferred ? undefined : (rel, isSource, newClass) => this.handleRelationshipReconnect(rel, isSource, newClass)}
                                        allClasses={this.props.diagram.classes}
                                        onClassHover={isInferred ? undefined : (c) => this.handleReconnectClassHover(c)}
                                        highlightedClass={this.state.reconnectHoverClass}
                                        checkReconnectCompatibility={isInferred ? undefined : (newClass, isSource) => this.checkReconnectCompatibility(r, newClass, isSource)}
                                        isInferred={isInferred}
                                        relationshipStyle={this.props.diagram.relationshipStyle}
                                        anchorMode={this.props.diagram.anchorMode}
                                        selectedNodeUri={selectedNodeUri}
                                        highlightedPredicates={this.props.highlightedPredicates}
                                    />
                                );
                            })}
                        </g>
                        {this.state.picker.visible && this.state.picker.source && this.state.picker.target && (
                            <RelationshipCommandPaletteHierarchical
                                x={this.state.picker.position.x}
                                y={this.state.picker.position.y}
                                sourceLabel={this.state.picker.source.modelElement.name}
                                targetLabel={this.state.picker.target.modelElement.name}
                                sourceClassUri={getSchemaClassFromElement(this.state.picker.source.modelElement)?.uri}
                                targetClassUri={getSchemaClassFromElement(this.state.picker.target.modelElement)?.uri}
                                classHierarchy={this.state.classHierarchy || undefined}
                                onSelect={(node) => this.handlePickerSelect(node)}
                                onCancel={() => this.hideRelationshipPicker()}
                                taxonomy={this.state.relationshipTaxonomy || undefined}
                            />
                        )}
                        {this.state.classPicker.visible && this.state.classPicker.modelClass && (
                            <ClassPicker
                                x={this.state.classPicker.position.x}
                                y={this.state.classPicker.position.y}
                                conceptLabel={this.state.classPicker.modelClass.name}
                                onSelect={(node) => this.handleClassPickerSelect(node)}
                                onCancel={() => this.handleClassPickerCancel()}
                                taxonomy={this.state.classTaxonomy || undefined}
                            />
                        )}
                    </g>
                    <defs>
                        <pattern id="gridPattern" width="25" height="25" patternUnits="userSpaceOnUse" x="0" y="0"
                                 key={`grid-${isDark}`}>
                            <rect width={this.props.gridSize} height={this.props.gridSize} fill="none"
                                  stroke={theme.grid}></rect>
                        </pattern>
                        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceAlpha" stdDeviation="10" result="blur"></feGaussianBlur>
                            <feFlood color="black" result="color"></feFlood>
                            <feComposite in="color" in2="blur" operator="in" result="shadow"></feComposite>
                            <feOffset in="feComposite" dx="15" dy="15" result="offsetBlur"></feOffset>
                            <feMerge>
                                <feMergeNode in="offsetBlur"></feMergeNode>
                                <feMergeNode in="SourceGraphic"></feMergeNode>
                            </feMerge>
                        </filter>

                        {/* ArchiMate Relationship Markers - using context-stroke to inherit line color */}

                        {/* Composition: filled diamond at source */}
                        <marker id="compositionDiamond" viewBox="0 -5 10 10" refX="0" refY="0"
                                markerWidth="10" markerHeight="10" orient="auto">
                            <path d="M0,0L5,-5L10,0L5,5Z" fill="context-stroke" stroke="context-stroke" strokeWidth="1"/>
                        </marker>

                        {/* Aggregation: unfilled diamond at source */}
                        <marker id="aggregationDiamond" viewBox="0 -5 10 10" refX="0" refY="0"
                                markerWidth="10" markerHeight="10" orient="auto">
                            <path d="M0,0L5,-5L10,0L5,5Z" fill={theme.background} stroke="context-stroke" strokeWidth="1"/>
                        </marker>

                        {/* Assignment: filled dot at target */}
                        <marker id="assignmentDot" viewBox="-5 -5 10 10" refX="0" refY="0"
                                markerWidth="8" markerHeight="8" orient="auto">
                            <circle cx="0" cy="0" r="4" fill="context-stroke" stroke="context-stroke" strokeWidth="1"/>
                        </marker>

                        {/* Open arrowhead: for Serving, Influence, Flow, Triggering, Association */}
                        <marker id="openArrowhead" viewBox="0 -5 10 10" refX="10" refY="0"
                                markerWidth="8" markerHeight="8" orient="auto">
                            <path d="M0,-4L10,0L0,4" fill="none" stroke="context-stroke" strokeWidth="1.5"/>
                        </marker>

                        {/* Open arrowhead reversed: for sourceMarker use - arrow points back toward source */}
                        <marker id="openArrowheadSource" viewBox="0 -5 10 10" refX="0" refY="0"
                                markerWidth="8" markerHeight="8" orient="auto">
                            <path d="M10,-4L0,0L10,4" fill="none" stroke="context-stroke" strokeWidth="1.5"/>
                        </marker>

                        {/* Hollow triangular arrowhead: for Realization, Specialization */}
                        <marker id="hollowTriangle" viewBox="0 -5 10 10" refX="10" refY="0"
                                markerWidth="10" markerHeight="10" orient="auto">
                            <path d="M0,-5L10,0L0,5Z" fill={theme.background} stroke="context-stroke" strokeWidth="1"/>
                        </marker>

                        {/* Access bar: for read access */}
                        <marker id="accessBar" viewBox="-2 -5 4 10" refX="0" refY="0"
                                markerWidth="4" markerHeight="10" orient="auto">
                            <path d="M0,-4L0,4" fill="none" stroke="context-stroke" strokeWidth="1.5"/>
                        </marker>

                        {/* Junction AND: filled circle */}
                        <marker id="junctionAnd" viewBox="-6 -6 12 12" refX="0" refY="0"
                                markerWidth="12" markerHeight="12" orient="auto">
                            <circle cx="0" cy="0" r="5" fill="context-stroke" stroke="context-stroke" strokeWidth="1"/>
                        </marker>

                        {/* Junction OR: unfilled circle */}
                        <marker id="junctionOr" viewBox="-6 -6 12 12" refX="0" refY="0"
                                markerWidth="12" markerHeight="12" orient="auto">
                            <circle cx="0" cy="0" r="5" fill={theme.background} stroke="context-stroke" strokeWidth="1"/>
                        </marker>

                        {/* Legacy markers for backwards compatibility */}
                        <marker id="arrowhead" viewBox="0 -5 10 10" refX="10" refY="0" markerWidth="6" markerHeight="6"
                                orient="auto">
                            <path d="M0,-5L10,0L0,5" fill="white" stroke="black"></path>
                        </marker>
                        <marker id="compositeDiamond" viewBox="0 -5 10 10" refX="0" refY="0" markerWidth="15"
                                markerHeight="15" orient="auto">
                            <path d="M0,0L5,5L10,0L5,-5Z" fill="white"></path>
                        </marker>
                        <marker id="generalizationArrowhead" viewBox="0 -5 10 10" refX="0" refY="0" markerWidth="15"
                                markerHeight="15" orient="auto">
                            <path d="M0,0L10,-5L10,5L0,0Z" stroke="white"></path>
                        </marker>
                        <marker id="sharedDiamond" viewBox="0 -5 10 10" refX="0" refY="0" markerWidth="15"
                                markerHeight="15" orient="auto">
                            <path d="M0,0L5,5L10,0L5,-5Z" stroke="white"></path>
                        </marker>

                        {/* Glow filters for knowledge-graph style relationships */}
                        <filter id="lineGlow" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="2.5" result="blur"/>
                            <feMerge>
                                <feMergeNode in="blur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>

                        <filter id="lineGlowHover" x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur in="SourceGraphic" stdDeviation="4" result="blur"/>
                            <feMerge>
                                <feMergeNode in="blur"/>
                                <feMergeNode in="SourceGraphic"/>
                            </feMerge>
                        </filter>

                        {/* Soft universal markers (fallback for knowledge-graph style) */}
                        <marker id="softDot" viewBox="-4 -4 8 8" refX="0" refY="0"
                                markerWidth="8" markerHeight="8" orient="auto">
                            <circle cx="0" cy="0" r="2.5" fill="context-stroke" opacity="0.85"/>
                        </marker>

                        <marker id="softArrow" viewBox="0 -4 8 8" refX="7" refY="0"
                                markerWidth="8" markerHeight="8" orient="auto">
                            <path d="M0,-3 L6,0 L0,3" fill="none" stroke="context-stroke"
                                  strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </marker>
                    </defs>
                </svg>
                {this.props.diagram.showToolbar && (
                    <div style={toolbarStyle}>
                        <button style={this.props.diagram.showGrid ? buttonOn : buttonOff} onClick={() => this.toggleGrid()}>
                            <span style={{opacity: 0.7}}>⊞</span> Grid
                        </button>
                        <button style={this.props.diagram.snapToGrid ? buttonOn : buttonOff} onClick={() => this.toggleSnap()}>
                            <span style={{opacity: 0.7}}>⊡</span> Snap
                        </button>
                        <button style={this.props.diagram.forceLayoutEnabled ? buttonOn : buttonOff} onClick={this.toggleForceLayout} title="Toggle force-directed auto-layout">
                            <span style={{opacity: 0.7}}>⟲</span> Force
                        </button>
                        <button style={this.props.diagram.showDomainVisualization ? buttonOn : buttonOff} onClick={() => this.toggleDomainVisualization()} title="Toggle domain visualization">
                            <span style={{opacity: 0.7}}>◎</span> Domain
                        </button>
                        <span style={{width: 1, background: 'rgba(255,255,255,0.1)', margin: '0 2px'}}></span>
                        <select
                            style={selectStyle}
                            value={this.props.diagram.relationshipStyle}
                            onChange={(e) => this.handleRelationshipStyleChange(e.target.value as RelationshipStyle)}
                            title="Relationship curve style"
                        >
                            <option value="s-curve">S-Curve</option>
                            <option value="arc">Arc</option>
                        </select>
                        <select
                            style={selectStyle}
                            value={this.props.diagram.anchorMode}
                            onChange={(e) => this.handleAnchorModeChange(e.target.value as AnchorMode)}
                            title="Anchor positioning mode"
                        >
                            <option value="auto">Auto</option>
                            <option value="dynamic">Dynamic</option>
                            <option value="centered">Centered</option>
                        </select>
                        <span style={{width: 1, background: 'rgba(255,255,255,0.1)', margin: '0 2px'}}></span>
                        <button style={buttonAction} onClick={() => this.fitToContent()}>Fit</button>
                        <button style={buttonAction} onClick={() => this.resetZoom()}>Reset</button>
                        <span style={buttonInfo}>{Math.round(this.state.zoomK * 100)}%</span>
                        <span style={{width: 1, background: 'rgba(255,255,255,0.1)', margin: '0 2px'}}></span>
                        {hasHiddenClasses && (
                            <button
                                style={buttonAction}
                                onClick={() => this.props.onUnhideAll?.()}
                                title="Show all hidden elements"
                            >
                                Unhide All
                            </button>
                        )}
                        <button style={this.state.showHelp ? buttonOn : buttonAction} onClick={() => this.toggleHelp()}>?</button>
                    </div>
                )}
                {this.state.showHelp && (
                    <div style={helpStyle}>
                        <div><strong>Shortcuts & Tips</strong></div>
                        <ul style={{margin: '6px 0 0 16px'}}>
                            <li>Drag classes to move them</li>
                            <li>Hold Shift to temporarily disable snapping</li>
                            <li>Double-click empty space to reset zoom</li>
                            <li>Press T to toggle toolbar visibility</li>
                            <li>Press F or toggle Force for auto-layout; drag to reposition</li>
                            <li>Select a class to show relationship markers; drag a marker onto another class</li>
                            <li>Drag relationship anchors to reposition; Ctrl/Cmd+click to reset</li>
                            <li>Press Delete to remove selected item</li>
                            <li>Press H to hide selected class (use Unhide All to restore)</li>
                        </ul>
                    </div>
                )}
                {/* Name Editor - rendered outside SVG with fixed positioning */}
                {this.state.nameEditor.visible && this.state.nameEditor.modelClass && getSchemaClassFromElement(this.state.nameEditor.modelClass) && (
                    <NameEditor
                        x={this.state.nameEditor.position.x}
                        y={this.state.nameEditor.position.y}
                        stereotype={getSchemaClassFromElement(this.state.nameEditor.modelClass)!.stereotype || getSchemaClassFromElement(this.state.nameEditor.modelClass)!.label || getSchemaClassFromElement(this.state.nameEditor.modelClass)!.name}
                        defaultName={this.state.nameEditor.defaultName}
                        onConfirm={(name) => this.handleNameEditorConfirm(name)}
                        onCancel={() => this.handleNameEditorCancel()}
                    />
                )}
                {/* Reconnection Confirmation Dialog */}
                {this.state.reconnectConfirm.visible && (
                    <ConfirmDialog
                        title="Constraint Mismatch"
                        message={this.state.reconnectConfirm.message}
                        confirmLabel="Reconnect Anyway"
                        cancelLabel="Cancel"
                        variant="warning"
                        onConfirm={this.handleReconnectConfirm}
                        onCancel={this.handleReconnectCancel}
                    />
                )}
            </div>
        )
    }

    stopDrag = () => {
        if (this.dragActiveElement) {
            this.dragActive = false;
            this.dragActiveElement.remove();
            this.dragActiveElement = null;
            this.dragActiveModel = null;
            this.props.onPlacementCancelled?.();
            this.forceUpdate(); // Force re-render to update className
        }
    }

    componentDidUpdate(prevProps: Readonly<Props>, _prevState: Readonly<State>) {
        // Reload domain data if domain or taxonomy props changed
        const domainChanged = prevProps.domainTtl !== this.props.domainTtl || prevProps.domainNs !== this.props.domainNs;
        const taxonomyChanged = prevProps.relationshipTaxonomy !== this.props.relationshipTaxonomy ||
            prevProps.classTaxonomy !== this.props.classTaxonomy ||
            prevProps.dataPropertiesMap !== this.props.dataPropertiesMap ||
            prevProps.objectPropertyRestrictionsMap !== this.props.objectPropertyRestrictionsMap ||
            prevProps.classHierarchy !== this.props.classHierarchy;

        if (domainChanged || taxonomyChanged) {
            console.debug("Domain or taxonomy changed, reloading data");
            this.loadDomainData();
        }


        // Handle diagram object change (e.g., when loading an example)
        if (prevProps.diagram !== this.props.diagram) {
            // Restore viewport transform from the new diagram
            const vt = this.props.diagram.viewportTransform;
            if (vt && (vt.x !== 0 || vt.y !== 0 || vt.k !== 1)) {
                const svg = d3.select(this.svg.current);
                svg.call(this.zoomBehavior.transform as any, d3.zoomIdentity.translate(vt.x, vt.y).scale(vt.k));
            }

            // Sync force layout state with the new diagram's state
            const wasForceEnabled = this.forceLayoutEngine !== null;
            if (this.props.diagram.forceLayoutEnabled !== wasForceEnabled) {
                if (this.props.diagram.forceLayoutEnabled) {
                    // New diagram wants force layout enabled - start with low alpha to preserve positions
                    this.setForceLayout(true, false, 0.01);
                } else {
                    // New diagram doesn't have force layout - stop it
                    this.setForceLayout(false, false);
                }
            } else if (this.props.diagram.forceLayoutEnabled && this.forceLayoutEngine) {
                // Force layout is enabled on both, but diagram changed - restart with low alpha
                this.forceLayoutEngine.stop();
                const svgEl = this.svg.current;
                const centerX = svgEl ? svgEl.clientWidth / 2 : 400;
                const centerY = svgEl ? svgEl.clientHeight / 2 : 300;
                this.forceLayoutEngine.setCenter(centerX, centerY);
                this.forceLayoutEngine.start(
                    this.props.diagram.classes,
                    this.props.diagram.getAllRelationships(),
                    0.01  // Low alpha to preserve existing positions
                );
            }
        }
        // Handle forceLayoutEnabled change on the same diagram (e.g., when loading example)
        else {
            const wasForceEnabled = this.forceLayoutEngine !== null;
            if (this.props.diagram.forceLayoutEnabled !== wasForceEnabled) {
                if (this.props.diagram.forceLayoutEnabled) {
                    // Force layout was just enabled - start with higher alpha for new layout
                    this.setForceLayout(true, false, 0.5);
                } else {
                    // Force layout was just disabled - stop it
                    this.setForceLayout(false, false);
                }
            }
        }

        // Update force simulation if diagram structure changed while force layout is enabled
        if (this.props.diagram.forceLayoutEnabled && this.forceLayoutEngine) {
            const prevClassCount = prevProps.diagram.classes.length;
            const currClassCount = this.props.diagram.classes.length;
            // Use getAllRelationships() to include inferred relationships
            const prevRelCount = prevProps.diagram.getAllRelationships().length;
            const currRelCount = this.props.diagram.getAllRelationships().length;

            if (prevClassCount !== currClassCount || prevRelCount !== currRelCount) {
                // Reinitialize simulation with new structure (including inferred relationships)
                // Use moderate alpha so layout adjusts to new nodes but doesn't drastically rearrange
                this.forceLayoutEngine.stop();
                this.forceLayoutEngine.start(
                    this.props.diagram.classes,
                    this.props.diagram.getAllRelationships(),
                    0.5
                );
            }

            // Notify force layout if property visibility changed (affects node dimensions)
            if (this.forceLayoutEngine.isRunning() && (
                prevProps.showOptionalProperties !== this.props.showOptionalProperties ||
                prevProps.showEssentialProperties !== this.props.showEssentialProperties ||
                prevProps.showStereotype !== this.props.showStereotype)) {
                this.forceLayoutEngine.notifyDimensionsChanged();
            }
        }
    }

    componentWillUnmount() {
        // Clean up force layout engine
        if (this.forceLayoutEngine) {
            this.forceLayoutEngine.stop();
            this.forceLayoutEngine = null;
        }
        // Remove resize listener
        window.removeEventListener("resize", () => this.onResize());
        // Remove keyboard event listener
        document.removeEventListener("keydown", this.handleKeyDown);
    }

    loadDomainData() {
        // If taxonomy data is provided as props, use it directly (no re-parsing!)
        if (this.props.relationshipTaxonomy) {
            console.debug("Using relationship taxonomy from props:", this.props.relationshipTaxonomy.length, "items");
            this.setState({ relationshipTaxonomy: this.props.relationshipTaxonomy });
        }
        if (this.props.classTaxonomy) {
            console.debug("Using class taxonomy from props:", this.props.classTaxonomy.length, "items");
            this.setState({ classTaxonomy: this.props.classTaxonomy });
        }
        if (this.props.dataPropertiesMap) {
            console.debug("Using data properties from props:", this.props.dataPropertiesMap.size, "classes");
            this.setState({ dataPropertiesMap: this.props.dataPropertiesMap });
        }
        if (this.props.objectPropertyRestrictionsMap) {
            console.debug("Using object property restrictions from props:", this.props.objectPropertyRestrictionsMap.size, "classes");
            this.setState({ objectPropertyRestrictionsMap: this.props.objectPropertyRestrictionsMap });
        }
        if (this.props.classHierarchy) {
            console.debug("Using class hierarchy from props:", this.props.classHierarchy.size, "entries");
            this.setState({ classHierarchy: this.props.classHierarchy });
        }

        // If any data is missing, fall back to loading from TTL
        const needsRelationship = !this.props.relationshipTaxonomy;
        const needsClass = !this.props.classTaxonomy;
        const needsDataProps = !this.props.dataPropertiesMap;
        const needsObjPropRestrictions = !this.props.objectPropertyRestrictionsMap;
        const needsHierarchy = !this.props.classHierarchy;

        if (!needsRelationship && !needsClass && !needsDataProps && !needsObjPropRestrictions && !needsHierarchy) {
            // All data provided via props, no TTL parsing needed
            return;
        }

        // Use allowedNamespaces if provided, otherwise fall back to just domainNs
        const namespaces = this.props.allowedNamespaces || [this.props.domainNs];

        // Only load what's missing
        if (needsRelationship) {
            loadRelationshipTaxonomy(this.props.domainTtl, this.props.domainNs, namespaces)
                .then(taxonomy => {
                    console.debug("Loaded relationship taxonomy from TTL:", taxonomy);
                    this.setState({ relationshipTaxonomy: taxonomy });
                })
                .catch(err => {
                    console.error("Failed to load relationship ontology:", err);
                });
        }

        if (needsClass) {
            loadClassTaxonomy(this.props.domainTtl, this.props.domainNs, namespaces)
                .then(taxonomy => {
                    console.debug("Loaded class taxonomy from TTL:", taxonomy);
                    this.setState({ classTaxonomy: taxonomy });
                })
                .catch(err => {
                    console.error("Failed to load class ontology:", err);
                });
        }

        if (needsDataProps) {
            loadDataProperties(this.props.domainTtl, this.props.domainNs, namespaces)
                .then(dataPropertiesMap => {
                    console.debug("Loaded data properties from TTL:", dataPropertiesMap);
                    this.setState({ dataPropertiesMap });
                })
                .catch(err => {
                    console.error("Failed to load data properties:", err);
                });
        }

        if (needsObjPropRestrictions) {
            loadObjectPropertyRestrictions(this.props.domainTtl, this.props.domainNs, namespaces)
                .then(objectPropertyRestrictionsMap => {
                    console.debug("Loaded object property restrictions from TTL:", objectPropertyRestrictionsMap);
                    this.setState({ objectPropertyRestrictionsMap });
                })
                .catch(err => {
                    console.error("Failed to load object property restrictions:", err);
                });
        }

        if (needsHierarchy) {
            loadClassHierarchy(this.props.domainTtl)
                .then(classHierarchy => {
                    console.debug("Loaded class hierarchy from TTL:", classHierarchy);
                    this.setState({ classHierarchy });
                })
                .catch(err => {
                    console.error("Failed to load class hierarchy:", err);
                });
        }
    }

    toggleGrid() {
        this.props.diagram.showGrid = !this.props.diagram.showGrid;
        this.props.onDiagramChanged?.();
        this.forceUpdate();
    }
    toggleToolbar() {
        this.props.diagram.showToolbar = !this.props.diagram.showToolbar;
        this.props.onDiagramChanged?.();
        this.forceUpdate();
    }
    toggleSnap() {
        this.props.diagram.snapToGrid = !this.props.diagram.snapToGrid;
        this.props.onDiagramChanged?.();
        this.forceUpdate();
    }
    toggleDomainVisualization() {
        this.props.diagram.showDomainVisualization = !this.props.diagram.showDomainVisualization;
        this.props.onDiagramChanged?.();
        this.forceUpdate();
    }

    /**
     * Prepare blob elements for the LiquidBlobLayer from visible classes.
     * Elements are grouped by their source: 'kg' for active KG, 'tbox' for OntologyModel elements.
     */
    prepareBlobElements(classes: DiagramClass[]): BlobElement[] {
        return classes.map(c => {
            // Get namespace tag prefix for width calculation
            const namespaceTag = c.sourceGraphIRI ? this.props.getNamespaceTag?.(c.sourceGraphIRI) : undefined;
            const dims = DiagramClassElement.calculateDimensions(
                c.modelElement,
                this.props.diagram.showOptionalProperties,
                this.props.diagram.showEssentialProperties,
                this.props.getClassLabel,
                namespaceTag?.prefix,
                (classUri) => this.state.dataPropertiesMap?.get(classUri),
                (classUri) => this.state.objectPropertyRestrictionsMap?.get(classUri)
            );
            // Elements with sourceGraphIRI are from OntologyModel (TBox)
            const groupId = c.sourceGraphIRI ? 'tbox' : 'kg';
            return {
                x: c.x + dims.width / 2,
                y: c.y + dims.height / 2,
                width: dims.width,
                height: dims.height,
                groupId: groupId
            };
        });
    }

    toggleHelp() { this.setState({showHelp: !this.state.showHelp}); }

    handleRelationshipStyleChange(style: RelationshipStyle) {
        this.props.diagram.setRelationshipStyle(style);
        this.props.onDiagramChanged?.();
        this.forceUpdate();
    }

    handleAnchorModeChange(mode: AnchorMode) {
        this.props.diagram.setAnchorMode(mode);
        this.props.onDiagramChanged?.();
        this.forceUpdate();
    }

    toggleForceLayout = () => {
        this.setForceLayout(!this.props.diagram.forceLayoutEnabled);
    }

    /**
     * Enable or disable force layout explicitly.
     * @param enabled - Whether to enable or disable force layout
     * @param persist - Whether to persist the change (default true)
     * @param initialAlpha - Initial energy level for the simulation (default 1). Use low values (e.g., 0.01) when restoring to preserve positions.
     */
    setForceLayout = (enabled: boolean, persist: boolean = true, initialAlpha: number = 1) => {
        if (enabled) {
            // Get viewport center for the centering force
            const svgEl = this.svg.current;
            const centerX = svgEl ? svgEl.clientWidth / 2 : 400;
            const centerY = svgEl ? svgEl.clientHeight / 2 : 300;

            // Create and start the force layout engine
            this.forceLayoutEngine = new ForceLayoutEngine(
                (diagramClass, x, y) => this.props.onClassMoved(diagramClass, x, y),
                () => this.forceUpdate()
            );
            this.forceLayoutEngine.setCenter(centerX, centerY);
            // Use getAllRelationships() to include inferred relationships in force simulation
            this.forceLayoutEngine.start(
                this.props.diagram.classes,
                this.props.diagram.getAllRelationships(),
                initialAlpha
            );
        } else {
            // Stop and clean up the force layout engine
            this.forceLayoutEngine?.stop();
            this.forceLayoutEngine = null;
        }

        // Update diagram model (for persistence)
        this.props.diagram.forceLayoutEnabled = enabled;
        if (persist) {
            this.props.onDiagramChanged?.();
        }
        this.forceUpdate();
    }

    /**
     * Restart the force simulation with the current diagram elements.
     * Call this after significantly modifying the diagram content (e.g., loading an example).
     */
    restartForceSimulation = (alpha: number = 0.5) => {
        if (!this.props.diagram.forceLayoutEnabled || !this.forceLayoutEngine) {
            return;
        }
        this.forceLayoutEngine.stop();
        this.forceLayoutEngine.start(
            this.props.diagram.classes,
            this.props.diagram.getAllRelationships(),
            alpha
        );
    }

    handleKeyDown = (event: KeyboardEvent) => {
        // Ignore if typing in an input field
        const target = event.target as HTMLElement;
        if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) {
            return;
        }

        // Handle F2 to rename selected element
        if (event.key === 'F2') {
            event.preventDefault();
            this.initiateRename();
        }

        if (event.key === 'f' || event.key === 'F') {
            event.preventDefault();
            this.toggleForceLayout();
        }

        // Handle 't' key to toggle toolbar visibility
        if (event.key === 't' || event.key === 'T') {
            event.preventDefault();
            this.toggleToolbar();
        }

        // Handle 'h' key to hide selected class
        if (event.key === 'h' || event.key === 'H') {
            event.preventDefault();
            this.hideSelectedClass();
        }

        // Handle Delete/Backspace to delete selected
        if (event.key === 'Delete' || event.key === 'Backspace') {
            event.preventDefault();
            this.deleteSelected();
        }

        // Handle Ctrl/Cmd+S to force save
        if ((event.ctrlKey || event.metaKey) && (event.key === 's' || event.key === 'S')) {
            event.preventDefault();
            this.props.onDiagramChanged?.();
        }
    }

    hideSelectedClass() {
        const { selectedClass } = this.state;
        if (!selectedClass) {
            console.debug("No class selected to hide");
            return;
        }

        // Get the underlying DiagramClass from the DiagramClassElement
        const diagramClass = selectedClass.props.diagramClass;
        console.log("Hiding class: " + diagramClass.modelElement.name);

        // Call the onClassHidden callback
        this.props.onClassHidden?.(diagramClass);

        // Clear selection
        this.setState({ selectedClass: null });
        this.props.onClassSelected(null);
    }

    /**
     * Initiate renaming the selected element via F2 key.
     * Mode-aware: In ABox mode, only Individuals can be renamed.
     * In TBox mode, only SchemaClasses can be renamed.
     */
    initiateRename() {
        const { selectedClass } = this.state;
        if (!selectedClass) {
            console.debug("No class selected to rename");
            return;
        }

        const modelElement = selectedClass.props.diagramClass.modelElement;
        const editingMode = this.props.editingMode || 'ABox';

        // Mode-aware validation
        if (editingMode === 'ABox' && !(modelElement instanceof Individual)) {
            console.debug("In ABox mode, only Individuals can be renamed");
            return;
        }
        if (editingMode === 'TBox' && modelElement instanceof Individual) {
            console.debug("In TBox mode, Individuals cannot be renamed");
            return;
        }

        // Get screen coordinates from the SVG element
        const svgElement = selectedClass.svgClass.current;
        if (!svgElement) {
            console.debug("Could not get SVG element for positioning");
            return;
        }

        const rect = svgElement.getBoundingClientRect();
        const screenX = rect.left + rect.width / 2;
        const screenY = rect.top + rect.height / 2;

        // Show name editor in rename mode
        this.setState({
            nameEditor: {
                visible: true,
                modelClass: modelElement,
                position: { x: screenX, y: screenY },
                defaultName: modelElement.name
            },
            isRenaming: true
        });
    }

    resetZoom() {
        const svg = d3.select(this.svg.current);
        if (!svg.empty()) {
            svg.call(this.zoomBehavior.transform as any, d3.zoomIdentity);
        }
    }

    fitToContent() {
        const svgEl = this.svg.current;
        const classesEl = this.svgClasses.current;
        if (!svgEl || !classesEl) return;
        const bbox = classesEl.getBBox();
        const svgRect = svgEl.getBoundingClientRect();
        const paddingX = 40;
        const paddingTop = 60;  // Extra space for toolbar
        const paddingBottom = 40;
        const w = bbox.width || 1;
        const h = bbox.height || 1;
        const innerW = Math.max(1, svgRect.width - paddingX * 2);
        const innerH = Math.max(1, svgRect.height - paddingTop - paddingBottom);
        const k = Math.max(0.1, Math.min(4, Math.min(innerW / w, innerH / h)));
        const tx = paddingX - k * bbox.x;
        const ty = paddingTop - k * bbox.y;
        const svg = d3.select(this.svg.current);
        svg.call(this.zoomBehavior.transform as any, d3.zoomIdentity.translate(tx, ty).scale(k));
    }

    handleViewportZoom = (event: d3.D3ZoomEvent<SVGGElement, any>) => {
        d3.select(this.svgViewport.current).attr("transform", event.transform.toString());
        // move grid rect to the opposite direction of the zoom
        const scale = event.transform.k;
        const tx = event.transform.x;
        const ty = event.transform.y;
        const x = -tx / scale;
        const y = -ty / scale;
        d3.select("#grid")
            .attr("x", -50000 + x)
            .attr("y", -50000 + y);
        if (this.state.zoomK !== scale) {
            this.setState({zoomK: scale});
        }
        // Update diagram's viewport transform (in memory only, persisted when other changes trigger save)
        this.props.diagram.setViewportTransform({ x: tx, y: ty, k: scale });
    };

    handleDiagramClick = (event: React.MouseEvent<SVGSVGElement, MouseEvent>) => {
        console.log("handleDiagramClick fired", {
            dragActive: this.dragActive,
            zoomMoveDistance: this.zoomMoveDistance,
            threshold: this.CLICK_MOVE_THRESHOLD
        });

        // Close pickers if open and click is outside of them
        if (this.state.picker.visible || this.state.classPicker.visible) {
            // Check if click is inside a picker (foreignObject or its children)
            let target = event.target as HTMLElement | null;
            let isInsidePicker = false;
            while (target) {
                if (target.tagName?.toLowerCase() === 'foreignobject') {
                    isInsidePicker = true;
                    break;
                }
                target = target.parentElement;
            }
            if (!isInsidePicker) {
                this.hideRelationshipPicker();
                this.hideClassPicker();
                return;
            }
        }

        if (this.dragActive && this.dragActiveElement) {
            // Placing a class on the diagram
            const [x, y] = d3.pointer(event, this.svgViewport.current);
            const model = this.dragActiveModel!;
            const snap = this.props.diagram.snapToGrid;
            // Get the rect dimensions to center properly
            const rect = this.dragActiveElement.select("rect");
            const width = rect.empty() ? 200 : parseFloat(rect.attr("width"));
            const height = rect.empty() ? 100 : parseFloat(rect.attr("height"));
            const finalX = snap ? snapToGrid(x - width / 2, this.props.gridSize) : (x - width / 2);
            const finalY = snap ? snapToGrid(y - height / 2, this.props.gridSize) : (y - height / 2);

            // Store pending placement
            this.pendingPlacement = { x: finalX, y: finalY, modelClass: model };

            // Get screen coordinates for UI elements (not affected by zoom)
            const screenX = event.clientX;
            const screenY = event.clientY;

            if (model instanceof Individual) {
                // Coming from toolbox - it's a ModelIndividual, show name editor
                // Use the pre-generated unique name from the model
                this.showNameEditor(model, { x: screenX, y: screenY }, model.name);
            } else if (model instanceof SchemaClass && (model.label || model.stereotype)) {
                // ModelClass with class info already set (from toolbox)
                // Use the pre-generated unique name from the model
                this.showNameEditor(model, { x: screenX, y: screenY }, model.name);
            } else {
                // Coming from concept chip - need to select class type first
                this.showClassPicker(model, { x, y });
            }
            this.stopDrag();
        } else if (this.zoomMoveDistance < this.CLICK_MOVE_THRESHOLD) {
            // Movement was below threshold - treat as a real click
            console.log("Movement below threshold, treating as click");
            let target = event.target as HTMLElement | null;
            let isClassOrRelationship = false;
            while (target) {
                const selection = d3.select(target);
                if (selection.classed("classes") || selection.classed("diagramClass") || selection.classed("diagramRelationship")) {
                    isClassOrRelationship = true;
                    break;
                }
                target = target.parentElement;
            }
            console.log("isClassOrRelationship:", isClassOrRelationship);
            if (!isClassOrRelationship) {
                console.log("Calling unselectAll()");
                this.unselectAll();
            }
        } else {
            console.log("Movement above threshold, ignoring click (pan/zoom occurred)");
        }

        // Reset for next interaction
        this.zoomStartPosition = null;
        this.zoomMoveDistance = 0;
    };

    handleDiagramMouseMove = (event: any) => {
        if (this.dragActive && this.dragActiveElement) {
            // move dragActiveElement to cursor position, centered on the placeholder
            const [x, y] = d3.pointer(event, d3.select(this.svgViewport.current).node());
            // Get the rect dimensions to center properly
            const rect = this.dragActiveElement.select("rect");
            const width = rect.empty() ? 200 : parseFloat(rect.attr("width"));
            const height = rect.empty() ? 100 : parseFloat(rect.attr("height"));
            this.dragActiveElement.attr("transform", `translate(${x - width / 2},${y - height / 2})`);
        }
    }

    handleContextMenu = (event: any) => {
        // Cancel drag and drop on right-click
        if (this.dragActive) {
            event.preventDefault();
            this.stopDrag();
        }
    }

    handleClassDragStart({x, y}: d3.D3DragEvent<any, any, any>, diagramClass: DiagramClassElement) {
        d3.select(diagramClass.svgClass.current).interrupt();

        const translate = getTranslateValues(d3.select(diagramClass.svgClass.current).attr('transform'));

        dx0 = x - translate.x;
        dy0 = y - translate.y;

        // Track drag start position to detect if it's a click or drag
        this.classDragStartPosition = {x, y};
        this.classDragDistance = 0;
        this.isDraggingClass = false;

        // Pin node during drag if force layout is active
        if (this.forceLayoutEngine?.isRunning()) {
            const dc = diagramClass.props.diagramClass;
            this.forceLayoutEngine.pinNode(dc, dc.x, dc.y);
        }
    }

    handleClassDragged({x, y, sourceEvent}: d3.D3DragEvent<any, any, any>, diagramClassElement: DiagramClassElement) {
        // Calculate how far we've dragged from the start
        if (this.classDragStartPosition) {
            const dx = x - this.classDragStartPosition.x;
            const dy = y - this.classDragStartPosition.y;
            this.classDragDistance = Math.sqrt(dx * dx + dy * dy);
        }

        // Only actually drag if we've moved beyond the threshold
        if (this.classDragDistance >= this.CLICK_MOVE_THRESHOLD) {
            this.isDraggingClass = true;

            // Disable grid snap during force layout
            const snap = this.props.diagram.snapToGrid && !sourceEvent.shiftKey && !this.props.diagram.forceLayoutEnabled;
            const finalX = snap ? snapToGrid(x - dx0, this.props.gridSize) : (x - dx0);
            const finalY = snap ? snapToGrid(y - dy0, this.props.gridSize) : (y - dy0);

            // Update pinned position if force layout is active
            if (this.forceLayoutEngine?.isRunning()) {
                this.forceLayoutEngine.updatePinnedNode(diagramClassElement.props.diagramClass, finalX, finalY);
            }

            this.props.onClassMoved(diagramClassElement.props.diagramClass, finalX, finalY);

            if (this.state.selectedClass == diagramClassElement)
                this.setState({"selectedClass": this.state.selectedClass});

            // Update the relationships
            diagramClassElement.props.diagramEditor.refreshRelationships();
        }
    }

    handleClassDragEnd(_event: d3.D3DragEvent<any, any, any>, diagramClassElement: DiagramClassElement) {
        console.log("handleClassDragEnd", {classDragDistance: this.classDragDistance, isDraggingClass: this.isDraggingClass});

        // Release pin on drop if force layout is active
        if (this.forceLayoutEngine?.isRunning()) {
            this.forceLayoutEngine.releaseNode(diagramClassElement.props.diagramClass);
        }

        // If we didn't actually drag (movement below threshold), treat as a click/select
        if (!this.isDraggingClass && this.classDragDistance < this.CLICK_MOVE_THRESHOLD) {
            console.log("Drag distance below threshold, treating as click - selecting class");
            this.unselectAll();
            this.selectClass(diagramClassElement);
        }

        // Reset drag tracking
        this.classDragStartPosition = null;
        this.classDragDistance = 0;
        this.isDraggingClass = false;
    }


    findClassByTarget(target: HTMLElement): DiagramClass | undefined {

        let el: HTMLElement | null = target;

        // detect which diagram-editor target is under the cursor
        while (el) {
            if (d3.select(el).classed("diagramClass")) {
                break;
            }
            el = el.parentElement;
        }
        if (el) {
            // find in classes the corresponding target
            return this.props.diagram.classes.find(c => c.modelElement.name === el.dataset.diagramClassName);
        }
        return undefined;
    }

    onResize() {

    }

    addDiagramClass(point: { x: number, y: number }, modelElement: Node) {
        // Calculate actual dimensions based on content
        // Use dynamic resolvers for property lookup (enables ontology switching)
        const { width, height } = DiagramClassElement.calculateDimensions(
            modelElement,
            true,
            true,
            this.props.getClassLabel,
            undefined,  // namespaceTagPrefix (not needed for new classes)
            (classUri) => this.state.dataPropertiesMap?.get(classUri),
            (classUri) => this.state.objectPropertyRestrictionsMap?.get(classUri)
        );
        const diagramClass = new DiagramClass(modelElement, point.x, point.y, width, height);
        //this.classes.push(new DiagramClassElement(this, diagramClass));
        this.props.onClassAdded(diagramClass);
        return diagramClass;
    }

    addRelationship(predicate: ObjectProperty, diagramClass1: DiagramClass, diagramClass2: DiagramClass) {
        const type = (predicate.relationshipType || 'association') as RELATIONSHIP_TYPE;
        console.log("Adding relationship: " + type + " between " + diagramClass1.modelElement.name + " and " + diagramClass2.modelElement.name)

        // Visual source/target matches semantic subject/object
        // Markers are positioned via visual annotations (sem:sourceMarker, sem:targetMarker)
        const modelRelationship = new Relationship(diagramClass1.modelElement, predicate, diagramClass2.modelElement);

        const diagramRelationship = new DiagramRelationship(modelRelationship, diagramClass1, diagramClass2);
        this.props.onRelationshipAdded(diagramRelationship);
    }

    selectClass(diagramClassElement: DiagramClassElement) {
        console.log({diagramClass: diagramClassElement, "this": this});
        // Unselect any selected relationship first
        this.unselectAllRelationships();
        this.setState({
            selectedClass: diagramClassElement
        });
        diagramClassElement.highlight(true);
        this.refreshRelationships();
        this.props.onClassSelected(diagramClassElement.props.diagramClass);
    }

    unselectAll() {
        this.unselectAllClasses();
        this.unselectAllRelationships();
    }

    unselectAllRelationships() {
        if (this.state.selectedRelationship) {
            this.setState({ selectedRelationship: null });
            this.props.onRelationshipSelected?.(null);
        }
        this.selectedRelationships = [];
    }

    unselectAllClasses() {
        this.state.selectedClass?.highlight(false);
        this.setState({selectedClass: null});
        this.props.onClassSelected(null);
        this.refreshRelationships();
    }

    refreshClasses() {
        //this.classes.forEach(c => c.refresh());
    }

    refreshRelationships() {
        this.relationships.forEach(_r => {
            //r.refresh();
        });
    }

    selectRelationship(diagramRelationship: DiagramRelationship) {
        // First unselect any selected class
        this.unselectAllClasses();
        // Set the selected relationship
        this.setState({ selectedRelationship: diagramRelationship });
        this.props.onRelationshipSelected?.(diagramRelationship);
    }

    /**
     * Handle class hover during relationship anchor reconnection.
     * Updates state to highlight the potential target class.
     */
    handleReconnectClassHover(diagramClass: DiagramClass | null) {
        this.setState({ reconnectHoverClass: diagramClass });
    }

    /**
     * Check if reconnecting a relationship to a class is compatible with domain/range constraints.
     * Used for real-time visual feedback during drag.
     */
    checkReconnectCompatibility(relationship: DiagramRelationship, newClass: DiagramClass, isSource: boolean): boolean {
        const predicate = relationship.modelRelationship.predicate;
        const constraint = isSource ? predicate.domain : predicate.range;

        const modelClass = getSchemaClassFromElement(newClass.modelElement);
        const newClassUri = modelClass?.uri;

        return this.isClassCompatibleWithConstraint(newClassUri, constraint);
    }

    /**
     * Check if a class is compatible with a class expression constraint.
     * Returns true if compatible, or if we can't verify (no constraint, no class hierarchy, etc.)
     */
    isClassCompatibleWithConstraint(classUri: string | undefined, constraint: OwlClassExpression | undefined): boolean {
        // If no constraint or no class URI, allow it
        if (!constraint || !classUri) return true;

        // If no class hierarchy loaded, allow it (can't verify)
        if (!this.state.classHierarchy) return true;

        // Check if class satisfies the constraint expression
        const hierarchy = this.state.classHierarchy;
        return classExpressionSatisfied(classUri, constraint, (uri, targetUri) =>
            isSubclassOf(uri, targetUri, hierarchy)
        );
    }

    /**
     * Handle relationship anchor reconnection.
     * Called when an anchor is dropped on a new class.
     * Checks domain/range compatibility and asks for confirmation if mismatched.
     */
    handleRelationshipReconnect(relationship: DiagramRelationship, isSource: boolean, newClass: DiagramClass) {
        // Get the predicate's domain/range constraints
        const predicate = relationship.modelRelationship.predicate;
        const constraint = isSource ? predicate.domain : predicate.range;
        const constraintType = isSource ? 'domain' : 'range';

        // Get the new class's URI
        const modelClassEl = getSchemaClassFromElement(newClass.modelElement);
        const newClassUri = modelClassEl?.uri;

        // Check compatibility
        const isCompatible = this.isClassCompatibleWithConstraint(newClassUri, constraint);

        if (!isCompatible) {
            // Get labels for a user-friendly message
            const relationshipName = relationship.modelRelationship.name;
            const newClassName = newClass.modelElement.name;
            const constraintLabel = getClassExpressionLabel(constraint);

            const message = `The ${constraintType} of "${relationshipName}" expects a "${constraintLabel}" class, but "${newClassName}" does not match this constraint.\n\nDo you want to reconnect anyway?`;

            // Show confirmation dialog instead of window.confirm
            this.setState({
                reconnectConfirm: {
                    visible: true,
                    relationship,
                    isSource,
                    newClass,
                    message
                }
            });
            return;
        }

        // Perform the reconnection
        this.performReconnection(relationship, isSource, newClass);
    }

    /**
     * Actually perform the reconnection after confirmation (or when no confirmation needed)
     */
    private performReconnection(relationship: DiagramRelationship, isSource: boolean, newClass: DiagramClass) {
        if (isSource) {
            relationship.reconnectSource(newClass);
        } else {
            relationship.reconnectTarget(newClass);
        }

        // Notify parent callback if provided
        this.props.onRelationshipReconnected?.(relationship, isSource, newClass);

        // Trigger diagram change to persist
        this.props.onDiagramChanged?.();

        // Clear hover state
        this.setState({ reconnectHoverClass: null });
    }

    /**
     * Handle confirmation of constraint mismatch reconnection
     */
    handleReconnectConfirm = () => {
        const { relationship, isSource, newClass } = this.state.reconnectConfirm;
        if (relationship && newClass) {
            this.performReconnection(relationship, isSource, newClass);
        }
        this.hideReconnectConfirm();
    }

    /**
     * Handle cancellation of reconnection
     */
    handleReconnectCancel = () => {
        // Clear hover state
        this.setState({ reconnectHoverClass: null });
        this.hideReconnectConfirm();
    }

    /**
     * Hide the reconnection confirmation dialog
     */
    hideReconnectConfirm() {
        this.setState({
            reconnectConfirm: {
                visible: false,
                relationship: null,
                isSource: false,
                newClass: null,
                message: ""
            }
        });
    }

    // Relationship Picker methods
    showRelationshipPicker(source: DiagramClass, target: DiagramClass, position: { x: number; y: number }) {
        this.setState({
            picker: {
                visible: true,
                source,
                target,
                position
            }
        });
    }

    hideRelationshipPicker() {
        this.setState({
            picker: {
                visible: false,
                source: null,
                target: null,
                position: { x: 0, y: 0 }
            }
        });
    }

    handlePickerSelect(node: RelationshipTaxonomyNode) {
        const { source, target } = this.state.picker;
        if (source && target && node.type) {
            // Create ObjectProperty from the taxonomy node
            const predicate: ObjectProperty = {
                uri: node.uri || `urn:objectproperty:${node.id}`,  // Use ontology URI, fallback to generated
                label: node.label,
                relationshipType: node.type,
                strokeStyle: node.strokeStyle,
                sourceMarker: node.sourceMarker,
                targetMarker: node.targetMarker,
                domain: node.domain,
                range: node.range
            };
            this.addRelationship(predicate, source, target);
        }
        this.hideRelationshipPicker();
    }

    // Class Picker methods (for selecting OWL2 class when placing a concept)
    showClassPicker(modelElement: Node, position: { x: number; y: number }) {
        this.setState({
            classPicker: {
                visible: true,
                modelClass: modelElement,
                position
            }
        });
    }

    hideClassPicker() {
        this.setState({
            classPicker: {
                visible: false,
                modelClass: null,
                position: { x: 0, y: 0 }
            }
        });
        // Clear pending placement
        this.pendingPlacement = null;
    }

    handleClassPickerSelect(node: ClassTaxonomyNode) {
        if (this.pendingPlacement) {
            const { x, y, modelClass } = this.pendingPlacement;
            // Get data properties for this class from the loaded map
            const dataProperties = this.state.dataPropertiesMap?.get(node.uri);
            // Get subClassOf axioms (ObjectPropertyRestrictions) for this class
            const subClassOf = this.state.objectPropertyRestrictionsMap?.get(node.uri);

            // Create a TBox ModelClass to reference from the individual
            // This uses the taxonomy node's class info
            const tboxModelClass = new SchemaClass('class', modelClass.concept, {
                label: node.label,
                stereotype: node.stereotype,
                dataProperties: dataProperties,
                subClassOf: subClassOf
            });

            // Create ModelIndividual (ABox) - concept chip selections become individuals
            const individualUri = `urn:individual:${Date.now()}`;
            const modelIndividual = new Individual(
                modelClass.concept,
                individualUri,
                modelClass.concept.prefLabel,
                tboxModelClass
            );

            // Now add the diagram class with the individual
            this.addDiagramClass({ x, y }, modelIndividual);
            this.pendingPlacement = null;
        }
        this.hideClassPicker();
    }

    handleClassPickerCancel() {
        // Cancel the placement entirely
        this.pendingPlacement = null;
        this.hideClassPicker();
        this.props.onPlacementCancelled?.();
    }

    // Name Editor methods (for naming a class from toolbox)
    showNameEditor(modelElement: Node, position: { x: number; y: number }, defaultName: string) {
        this.setState({
            nameEditor: {
                visible: true,
                modelClass: modelElement,
                position,
                defaultName
            }
        });
    }

    hideNameEditor() {
        this.setState({
            nameEditor: {
                visible: false,
                modelClass: null,
                position: { x: 0, y: 0 },
                defaultName: ""
            }
        });
        // Clear pending placement
        this.pendingPlacement = null;
    }

    handleNameEditorConfirm(name: string) {
        const { isRenaming, nameEditor } = this.state;
        const trimmedName = name.trim();

        // Handle rename mode
        if (isRenaming && nameEditor.modelClass) {
            if (trimmedName && trimmedName !== nameEditor.modelClass.name) {
                this.props.onElementRenamed?.(nameEditor.modelClass, trimmedName);
            }
            this.hideNameEditor();
            this.setState({ isRenaming: false });
            return;
        }

        // Handle create mode (original logic)
        if (this.pendingPlacement) {
            const { x, y, modelClass } = this.pendingPlacement;

            // Create the final concept with the user-provided name
            const finalConcept = new Concept(
                `urn:concept:${Date.now()}`,
                trimmedName,
                [],
                [],
                ""
            );

            let finalModel: Node;

            if (modelClass instanceof Individual) {
                // Generate new URI based on the user-provided name
                const newUri = this.props.generateIndividualUri
                    ? this.props.generateIndividualUri(trimmedName)
                    : modelClass.individualUri;  // Fallback to existing URI
                // Create new Individual with updated concept, URI, and label
                finalModel = new Individual(
                    finalConcept,
                    newUri,
                    trimmedName,
                    modelClass.modelClass       // Same TBox class reference
                );
            } else if (modelClass instanceof SchemaClass) {
                // Update ModelClass concept
                modelClass.concept = finalConcept;
                finalModel = modelClass;
            } else {
                // Fallback - shouldn't happen
                finalModel = modelClass;
            }

            // Add the diagram class
            this.addDiagramClass({ x, y }, finalModel);
            this.pendingPlacement = null;
        }
        this.hideNameEditor();
    }

    handleNameEditorCancel() {
        const wasRenaming = this.state.isRenaming;
        // Cancel the placement entirely
        this.pendingPlacement = null;
        this.hideNameEditor();
        this.setState({ isRenaming: false });
        // Only call placement cancelled if we weren't renaming
        if (!wasRenaming) {
            this.props.onPlacementCancelled?.();
        }
    }

    deleteSelectedClasses() {
        const selectedClassElement = this.state.selectedClass;
        if (!selectedClassElement) {
            console.debug("No class selected to delete");
            return;
        }

        const diagramClass = selectedClassElement.props.diagramClass;
        console.log("Deleting class: " + diagramClass.modelElement.name);

        // Get all relationships connected to this class (to delete them too)
        const connectedRelationships = this.props.diagram.getRelationshipsForClass(diagramClass);
        console.log(`Deleting ${connectedRelationships.length} connected relationships`);

        // Delete each connected relationship first
        connectedRelationships.forEach(rel => {
            this.props.onRelationshipDeleted?.(rel);
        });

        // Delete the class
        this.props.onClassDeleted?.(diagramClass);

        // Clear selection
        this.setState({ selectedClass: null });
        this.props.onClassSelected(null);
    }

    deleteSelectedRelationship() {
        const selectedRelationship = this.state.selectedRelationship;
        if (!selectedRelationship) {
            console.debug("No relationship selected to delete");
            return;
        }

        console.log("Deleting relationship: " + selectedRelationship.modelRelationship.name);

        // Delete the relationship
        this.props.onRelationshipDeleted?.(selectedRelationship);

        // Clear selection
        this.setState({ selectedRelationship: null });
        this.props.onRelationshipSelected?.(null);
    }

    deleteSelected() {
        // Delete whatever is currently selected (class takes priority)
        if (this.state.selectedClass) {
            this.deleteSelectedClasses();
        } else if (this.state.selectedRelationship) {
            this.deleteSelectedRelationship();
        }
    }

    createClassPlaceholder(conceptName?: string): d3.Selection<SVGGElement, any, any, any> {
        const theme = getTheme();
        const g = d3.select(this.svgClasses.current).append("g")
            .style("pointer-events", "none")
            .style("opacity", "0.7");

        const minWidth = 200;
        const height = 100;
        const padding = 40; // Horizontal padding for text

        // Create text first to measure it
        let textWidth = 0;
        if (conceptName) {
            const textEl = g.append("text")
                .attr("text-anchor", "middle")
                .attr("dominant-baseline", "middle")
                .attr("fill", theme.classBorderHover)
                .attr("font-size", "16")
                .attr("font-weight", "bold")
                .text(conceptName);

            // Measure text width
            const bbox = (textEl.node() as SVGTextElement).getBBox();
            textWidth = bbox.width;
        }

        // Calculate box width based on text
        const boxWidth = Math.max(minWidth, textWidth + padding);

        // Add rect with calculated width
        g.insert("rect", "text")
            .attr("width", boxWidth)
            .attr("height", height)
            .attr("stroke", theme.classBorderHover)
            .attr("stroke-width", 3)
            .attr("fill", "rgba(100, 200, 200, 0.1)")
            .attr("stroke-dasharray", "5,5")
            .attr("rx", 5);

        // Position text in center of box
        if (conceptName) {
            g.select("text")
                .attr("x", boxWidth / 2)
                .attr("y", height / 2);
        }

        return g as any;
    }
}

export {DiagramEditor}